import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Info, Plus } from 'lucide-react';
import { Button } from '@repo/ui/src/components/ui/button';
import { Checkbox } from '@repo/ui/src/components/ui/checkbox';
import { Label } from '@repo/ui/src/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@repo/ui/src/components/ui/dialog';
import { Drawer, DrawerContent, DrawerDescription, DrawerTitle } from '@repo/ui/src/components/ui/drawer';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@repo/ui/src/components/ui/tooltip';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@repo/ui/src/components/ui/alert-dialog';
import { useIsMobile } from '@repo/ui/src/hooks/use-mobile';
import type { ClassColor, ClassGroupListRow, ClassRosterRow } from '@repo/shared-types';
import { useClasses, useClass, useUpdateClassMemberSlots, useRemoveClassMember } from '../../hooks/useClasses';
import { useSubjects } from '../../hooks/useSubjects';
import { useMembers } from '../../hooks/useTeam';
import { useShowTeacher } from '../../hooks/useTeachers';
import { resolveTeacher, type TeacherLike } from '../../lib/teacherDisplay';
import { classColorStyle } from '../../lib/classColors';
import { groupSlotsByTime } from '../../lib/classSchedule';
import { capacityDisplay } from '../../lib/classCapacity';
import { displayNames } from '../../lib/studentName';
import { DatePicker } from '../../components/pickers/DatePicker';
import { ClassFormSheet } from './ClassFormSheet';
import { OneOffSessionForm } from '../schedules/OneOffSessionForm';
import { todayHouseIso } from '../../lib/houseToday';

// 'yyyy-mm-dd' -> 'dd/mm' for compact display on the join button.
function shortDate(iso: string): string {
  const [, m, d] = iso.split('-');
  return d && m ? `${d}/${m}` : iso;
}

type AssignT = (key: string, options?: Record<string, unknown>) => string;

/** 10px colour square (S7/2): filled tint+ink, or a dashed hollow square for
 *  "no colour yet" — same convention as `ClassListTab`'s `ColorSquare`, sized
 *  for this card. */
function CardColorSquare({ color }: { color: ClassColor | null }) {
  const style = classColorStyle(color);
  if (!style) {
    return <span className="h-2.5 w-2.5 shrink-0 rounded-[3px] border border-dashed border-surface-300" aria-hidden />;
  }
  return <span className="h-2.5 w-2.5 shrink-0 rounded-[3px] border opacity-90" style={style} aria-hidden />;
}

/** Up to 4 given names (collision-widened, `displayNames`), then "+N" for the
 *  rest — the muted roster line under each card (S7/2: "Bảo Châu, Nhật Minh,
 *  Tuệ Nhi, Gia Hân +9"). Null when the class has no members yet. */
function namesPreview(t: AssignT, roster: ClassRosterRow[] | undefined): string | null {
  if (!roster || roster.length === 0) return null;
  const MAX = 4;
  const shown = displayNames(roster.slice(0, MAX).map((r) => r.studentName));
  const rest = roster.length - shown.length;
  return rest > 0 ? `${shown.join(', ')} ${t('assign.membersMore', { count: rest })}` : shown.join(', ');
}

/**
 * One class card's live content, keyed by whether the enrollment is a member
 * (`ClassAssignCard` below remounts this the moment `useClass`'s roster
 * resolves that) so `checked` always lazy-initializes from the right subset:
 * every khung giờ when joining fresh, the enrollment's own live subset when
 * editing an existing membership — same "adjust state during render via a
 * key" technique `MemberSlotsPopover` already uses.
 */
function ClassAssignCardBody({
  groupRow, member, roster, studentName, enrollmentId, startDate, isPrimary, showTeacher, members,
}: {
  groupRow: ClassGroupListRow;
  member: ClassRosterRow | null;
  roster: ClassRosterRow[] | undefined;
  studentName: string;
  enrollmentId: string;
  startDate: string;
  /** Only the first card in the list gets the solid button — several joinable
   *  classes at once read better with one clear default action (S7/2: the
   *  first class card is primary, the second is outline). */
  isPrimary: boolean;
  showTeacher: boolean;
  members: TeacherLike[] | undefined;
}) {
  const { t } = useTranslation('scheduling');
  const updateSlots = useUpdateClassMemberSlots(groupRow.id);
  const removeMember = useRemoveClassMember(groupRow.id);
  const isMember = member != null;
  const allSlotIds = groupRow.slots.map((s) => s.id);
  const initialIds = isMember ? member.slotIds : allSlotIds;

  const [checked, setChecked] = useState<Set<string>>(() => new Set(initialIds));
  const [disclosureOpen, setDisclosureOpen] = useState(false);
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
  const [dateError, setDateError] = useState<string | null>(null);

  const groups = groupSlotsByTime(groupRow.slots);
  const scheduleText = groups.map((g) => `${g.days.map((d) => t(`days.${d}`)).join(', ')} ${g.time}`).join(' · ');
  const teacher = showTeacher && groupRow.teacherIds[0] ? resolveTeacher(members ?? [], groupRow.teacherIds[0]) : null;
  const capacity = capacityDisplay(groupRow.rosterCount, groupRow.maxCapacity, t);
  const isOver = groupRow.maxCapacity != null && groupRow.rosterCount > groupRow.maxCapacity;
  const names = namesPreview(t, roster);

  const newlyChecked = isMember ? [...checked].filter((id) => !initialIds.includes(id)) : [];
  const changedFromMember = isMember && (checked.size !== initialIds.length || initialIds.some((id) => !checked.has(id)));

  function toggle(slotId: string) {
    setDateError(null);
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(slotId)) next.delete(slotId); else next.add(slotId);
      return next;
    });
  }

  function handleJoin() {
    updateSlots.mutate(
      { enrollmentId, slotIds: [...checked], joinedAt: startDate },
      {
        onError: (err: Error & { code?: string }) => {
          if (err.code === 'START_DATE_IN_RECONCILED_MONTH' || err.code === 'START_DATE_BEFORE_CLASS') setDateError(err.message);
          // Any other error already toasts via useUpdateClassMemberSlots's own onError.
        },
      },
    );
  }

  function handleSaveSubset() {
    updateSlots.mutate(
      { enrollmentId, slotIds: [...checked], ...(newlyChecked.length > 0 ? { joinedAt: startDate } : {}) },
      {
        onError: (err: Error & { code?: string }) => {
          if (err.code === 'START_DATE_IN_RECONCILED_MONTH' || err.code === 'START_DATE_BEFORE_CLASS') setDateError(err.message);
        },
      },
    );
  }

  return (
    <div className="flex flex-col gap-2 p-[11.2px_12.8px]">
      <div className="flex flex-wrap items-center justify-between gap-[12.8px]">
        <span className="flex min-w-0 flex-wrap items-center gap-2 text-sm">
          <CardColorSquare color={groupRow.color} />
          <span className="font-semibold text-surface-900">{groupRow.displayName}</span>
          {scheduleText && <span className="text-[13px] text-surface-500">{scheduleText}</span>}
          {teacher && (
            <span className="inline-flex items-center gap-[4.8px] text-[12.5px] text-surface-500">
              <span className="h-2 w-2 rounded-full" style={{ background: teacher.color }} />
              {teacher.name}
            </span>
          )}
          {capacity.pill ? (
            <span className="tabular inline-flex h-5 items-center rounded-full bg-surface-100 px-[6.4px] text-[11.5px] font-semibold text-surface-600">
              {capacity.text}
            </span>
          ) : (
            <span className="tabular text-[12.5px] text-surface-600">{capacity.text}</span>
          )}
        </span>
        {isMember ? (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{t('assign.joined')} ✓</span>
            <Button size="sm" variant="outline" className="h-[40px] sm:h-[28.8px]" onClick={() => setLeaveConfirmOpen(true)}>
              {t('assign.leave')}
            </Button>
          </div>
        ) : (
          <Button
            size="sm"
            variant={isPrimary ? 'default' : 'outline'}
            className="h-[40px] whitespace-nowrap sm:h-[28.8px]"
            disabled={updateSlots.isPending || checked.size === 0}
            onClick={handleJoin}
          >
            {t('assign.joinFrom', { date: shortDate(startDate) })}
          </Button>
        )}
      </div>

      {names && (
        <div className="text-xs text-surface-500">
          {names}
          {/* Fix wave (review I6) — over capacity blocks nothing (assigning
              still works), so §6.15 reserves ochre for a state that
              actually blocks. Neutral tone, same as `capacityDisplay`'s own
              pill right above this line. */}
          {isOver && <> · <span className="text-surface-500">{t('assign.overCapacity')}</span></>}
        </div>
      )}

      {groupRow.slots.length > 1 && (
        <div className="flex flex-col gap-[6.4px] border-t border-dashed border-surface-200 pt-[4.8px]">
          <button
            type="button"
            onClick={() => setDisclosureOpen((o) => !o)}
            className="inline-flex w-fit items-center gap-[4.8px] text-[12.5px] font-medium text-brand-700"
          >
            {disclosureOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            {t('assign.onlySomeSlots')}
          </button>
          {disclosureOpen && (
            <div className="flex flex-col gap-[6.4px] pl-[18px]">
              <div className="flex flex-wrap gap-2">
                {groupRow.slots.map((s) => (
                  <label key={s.id} className="inline-flex items-center gap-[6.4px] text-[13px]">
                    <Checkbox checked={checked.has(s.id)} onCheckedChange={() => toggle(s.id)} className="h-4 w-4" />
                    {t(`days.${s.dayOfWeek}`)} <span className="tabular">{s.startTime}</span>
                  </label>
                ))}
              </div>
              <span className="text-xs text-surface-500">{t('assign.onlySomeSlotsNote')}</span>
              {isMember && (
                <div className="flex items-center gap-[6.4px]">
                  <Button
                    type="button"
                    size="sm"
                    className="h-[32px]"
                    disabled={!changedFromMember || checked.size === 0 || updateSlots.isPending}
                    onClick={handleSaveSubset}
                  >
                    {t('classDetail.popover.save')}
                  </Button>
                  {dateError && <span className="text-[12px] text-destructive">{dateError}</span>}
                </div>
              )}
              {!isMember && dateError && <span className="text-[12px] text-destructive">{dateError}</span>}
            </div>
          )}
        </div>
      )}

      <AlertDialog open={leaveConfirmOpen} onOpenChange={setLeaveConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('classDetail.removeConfirm.title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('classDetail.removeConfirm.body', { name: studentName })}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('slot.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => removeMember.mutate(enrollmentId, { onSuccess: () => setLeaveConfirmOpen(false) })}
              disabled={removeMember.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('classDetail.removeConfirm.action')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/** One class of the enrollment's subject: fetches its own roster (`useClass`)
 *  to know whether `enrollmentId` is already a member and, if so, of which
 *  khung giờ — the list endpoint (`useClasses`) carries counts and slots but
 *  not per-student membership. Remounted (`key`) the moment that resolves so
 *  `ClassAssignCardBody`'s `checked` state lazy-initializes from the right
 *  subset instead of a stale "not a member yet" default. */
function ClassAssignCard(props: {
  groupRow: ClassGroupListRow;
  enrollmentId: string;
  studentName: string;
  startDate: string;
  isPrimary: boolean;
  showTeacher: boolean;
  members: TeacherLike[] | undefined;
}) {
  const { data: detail } = useClass(props.groupRow.id);
  const member = detail?.roster.find((r) => r.enrollmentId === props.enrollmentId) ?? null;
  return (
    <ClassAssignCardBody
      key={member ? `m-${member.enrollmentId}` : 'new'}
      groupRow={props.groupRow}
      member={member}
      roster={detail?.roster}
      studentName={props.studentName}
      enrollmentId={props.enrollmentId}
      startDate={props.startDate}
      isPrimary={props.isPrimary}
      showTeacher={props.showTeacher}
      members={props.members}
    />
  );
}

/** The shared body of the dialog (desktop) / bottom sheet (phone): Ngày bắt
 *  đầu, the subject's open classes as cards, "Tạo lớp mới", then Buổi lẻ
 *  unchanged (S7/2, S7/3). */
function AssignDialogBody({
  enrollmentId, subjectId, subjectName, studentName, startDate, onStartDateChange, onCreateClass, onClose,
}: {
  enrollmentId: string;
  subjectId: string;
  subjectName: string;
  studentName: string;
  startDate: string;
  onStartDateChange: (v: string) => void;
  onCreateClass: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('scheduling');
  const { data: classRows } = useClasses({ subjectId, status: 'ACTIVE' });
  const { data: members } = useMembers();
  const showTeacher = useShowTeacher();

  const sorted = [...(classRows ?? [])].sort((a, b) => a.displayName.localeCompare(b.displayName, 'vi'));

  return (
    <div className="grid gap-4 py-2">
      <section className="grid gap-2">
        <div className="text-sm font-semibold">{t('assign.recurringTitle', { subject: subjectName })}</div>
        <p className="text-xs text-muted-foreground">{t('assign.recurringHint')}</p>
        <div className="grid gap-1 max-w-[220px]">
          <div className="flex items-center gap-1">
            <Label htmlFor="assignStartDate" className="text-xs">{t('assign.startDateLabel')}</Label>
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" tabIndex={-1} aria-label={t('assign.startDateHint')} className="text-muted-foreground hover:text-foreground">
                    <Info className="h-3.5 w-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-[240px] text-xs leading-snug">{t('assign.startDateHint')}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <DatePicker id="assignStartDate" value={startDate} onChange={onStartDateChange} />
        </div>

        {sorted.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('assign.emptyClasses', { subject: subjectName })}</p>
        ) : (
          <div className="rounded-md border border-surface-200 divide-y divide-surface-100">
            {sorted.map((row, i) => (
              <ClassAssignCard
                key={row.id}
                groupRow={row}
                enrollmentId={enrollmentId}
                studentName={studentName}
                startDate={startDate}
                isPrimary={i === 0}
                showTeacher={showTeacher}
                members={members}
              />
            ))}
          </div>
        )}

        <Button variant="outline" className="self-start" onClick={onCreateClass}>
          <Plus className="h-3.5 w-3.5" /> {t('assign.createClass')}
        </Button>
      </section>

      <section className="grid gap-2 border-t pt-3">
        <div className="text-sm font-semibold">{t('assign.oneOffTitle')}</div>
        <p className="text-xs text-muted-foreground">{t('assign.oneOffHint')}</p>
        <OneOffSessionForm enrollmentIds={[enrollmentId]} subjectId={subjectId} onCreated={onClose} />
      </section>
    </div>
  );
}

// Assign a specific enrollment (student+subject) to a class of the current subject.
export function AssignDialog({ open, onOpenChange, enrollmentId, subjectId, studentName }: {
  open: boolean; onOpenChange: (o: boolean) => void; enrollmentId: string; subjectId: string; studentName: string;
}) {
  const { t } = useTranslation('scheduling');
  const { data: subjects } = useSubjects();
  const subjectName = subjects?.find((s) => s.id === subjectId)?.name ?? '';
  const isMobile = useIsMobile();
  const [startDate, setStartDate] = useState(todayHouseIso());
  const [classSheetOpen, setClassSheetOpen] = useState(false);

  // Reset to today each time the dialog is (re)opened for a (possibly different) enrollment.
  // Adjusting state during render (rather than an effect) per React's guidance for
  // resetting state on a prop change: https://react.dev/learn/you-might-not-need-an-effect
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) setStartDate(todayHouseIso());
  }

  // "Tạo lớp mới" closes this dialog and opens ClassFormSheet as a sibling
  // (not nested inside DialogContent) — a Radix Sheet/vaul Drawer stacked
  // inside an already-open Dialog is two full-screen overlays fighting over
  // focus/scrim at once, unlike the old per-slot SlotFormDialog this replaces.
  // Saving reopens this dialog so the teacher lands back in Xếp lớp with the
  // new class visible (spec §5 "two ways in, one source of data").
  const openClassSheet = () => {
    onOpenChange(false);
    setClassSheetOpen(true);
  };
  const onClassSaved = () => {
    setClassSheetOpen(false);
    onOpenChange(true);
  };

  const body = (
    <AssignDialogBody
      enrollmentId={enrollmentId}
      subjectId={subjectId}
      subjectName={subjectName}
      studentName={studentName}
      startDate={startDate}
      onStartDateChange={setStartDate}
      onCreateClass={openClassSheet}
      onClose={() => onOpenChange(false)}
    />
  );

  return (
    <>
      {isMobile ? (
        <Drawer open={open} onOpenChange={onOpenChange}>
          {/* Content must NOT be the scroll container (§6.3) — the inner div
              below owns overflow-y-auto so vaul's own drag-vs-scroll detection
              still finds a scrollable child. */}
          <DrawerContent className="max-h-[85vh]">
            <DrawerTitle className="px-4 pt-1 text-[18px] font-semibold">
              {t('assign.title')} · {subjectName}
            </DrawerTitle>
            <DrawerDescription className="sr-only">{t('assign.recurringHint')}</DrawerDescription>
            <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-[calc(16px+env(safe-area-inset-bottom))]">
              {body}
            </div>
          </DrawerContent>
        </Drawer>
      ) : (
        <Dialog open={open} onOpenChange={onOpenChange}>
          {/* Scrolling is handled by DialogContent itself — see the note there. */}
          <DialogContent className="sm:max-w-[560px]">
            <DialogHeader><DialogTitle>{t('assign.title')} · {studentName}</DialogTitle></DialogHeader>
            {body}
          </DialogContent>
        </Dialog>
      )}
      <ClassFormSheet
        open={classSheetOpen}
        onOpenChange={setClassSheetOpen}
        defaultSubjectId={subjectId}
        onSaved={onClassSaved}
      />
    </>
  );
}
